import React, { useEffect, useState } from 'react';
import { Row, Col, Modal, Table } from 'react-bootstrap';
import LastMonthCostChart from './charts/LastMonthCostChart';
import RealTimeUsageChart from './charts/RealTimeUsageChart';
import TodayUsageChart from './charts/TodayUsageChart';
import UsageChart from './charts/UsageChart';
import BillingSummary from './charts/BillingSummary';
import BillingDetails from './charts/BillingDetails';
import { fetchBillingData, fetchBillingDetail } from 'hooks/CallApi';
import { isDemoMode } from 'data/demoMockData';


// 메모이제이션된 차트 컴포넌트들 - 이 부분은 안전함
const MemoizedLastMonthCostChart = React.memo(LastMonthCostChart);
const MemoizedRealTimeUsageChart = React.memo(RealTimeUsageChart);
const MemoizedTodayUsageChart = React.memo(TodayUsageChart);
const MemoizedUsageChart = React.memo(UsageChart);
const MemoizedBillingSummary = React.memo(BillingSummary);
const MemoizedBillingDetails = React.memo(BillingDetails);

const SERVICE_TYPES = [
    { key: 'sdk', label: 'SDK' },
    { key: 'cf', label: 'CF' },
    { key: 'api', label: 'API' }
];

const BillingDashboard = () => {
    const [billingData, setBillingData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedYearMonth, setSelectedYearMonth] = useState(null);
    const [detailsMap, setDetailsMap] = useState({});
    const [selectedMonthInfo, setSelectedMonthInfo] = useState(null);
    const [showNamespaceModal, setShowNamespaceModal] = useState(false);
    const [serviceType, setServiceType] = useState('sdk');

    // 기본 서비스 데이터 - 데이터가 없을 때 표시할 용도
    const defaultServiceData = {
        SDK: [
            { name: '예/적금', amount: 0 },
            { name: '입출금', amount: 0 },
            { name: '가입자 생성/조회', amount: 0 },
            { name: '대출', amount: 0 }
        ],
        CF: [
            { name: 'Cloud Function 호출', amount: 0 },
            { name: '배포 관리', amount: 0 }
        ],
        API: [
            { name: 'API Gateway', amount: 0 },
            { name: 'API 요청 처리', amount: 0 }
        ]
    };

    // 기본값 설정
    const defaultBillingData = {
        lastMonthCost: 0,
        thisMonthCost: 0,
        forecastCost: 0,
        billingDetails: {
            totalAmount: 0,
            finalAmount: 0,
            discount: 0,
            items: {
                CF: [],
                API: [],
                SDK: []
            }
        },
        monthlyData: [
            {
                month: 4,
                year: 2025,
                totalAmount: 0,
                finalAmount: 0,
                discount: 0,
                items: {
                    CF: [],
                    API: [],
                    SDK: []
                },
                totalRequests: 0,
                pricingPolicy: {
                    name: "",
                    tiers: []
                }
            },
            {
                month: 5,
                year: 2025,
                totalAmount: 0,
                finalAmount: 0,
                discount: 0,
                items: {
                    CF: [],
                    API: [],
                    SDK: []
                },
                totalRequests: 0,
                pricingPolicy: {
                    name: "",
                    tiers: []
                }
            }
        ],
        lastDailyCosts: Array.from({ length: 30 }, (_, index) => ({
            date: `2025-04-${String(index + 1).padStart(2, '0')}`,
            dailyFee: 0 // 모든 일별 비용을 0으로 설정
        }))
    };

    // useEffect(() => {
    //     const loadBillingData = async () => {
    //         setLoading(true);
    //         try {
    //             const response = await fetchBillingData();
    //             console.log('API Response:', response);

    //             // 데이터 유효성 검사
    //             const isValid =
    //                 response &&
    //                 response.data &&
    //                 Array.isArray(response.data.monthlyData) &&
    //                 response.data.monthlyData.length > 0 &&
    //                 response.data.monthlyData[0].year &&
    //                 response.data.monthlyData[0].month;

    //             if (isValid) {
    //                 setBillingData(response.data);

    //                 // monthlyData에서 최신 월 선택
    //                 const monthlyData = response.data.monthlyData;
    //                 const latestMonth = monthlyData.reduce((latest, current) => {
    //                     if (
    //                         current.year > latest.year ||
    //                         (current.year === latest.year && current.month > latest.month)
    //                     ) {
    //                         return current;
    //                     }
    //                     return latest;
    //                 }, monthlyData[0]);

    //                 setSelectedMonthInfo({ year: latestMonth.year, month: latestMonth.month });
    //             } else {
    //                 console.error('Invalid response format:', response);
    //                 setError('Invalid API response format');
    //                 setBillingData(defaultBillingData); // 기본값 설정
    //                 setSelectedMonthInfo({ year: 2025, month: 4 }); // 기본값으로 2025년 4월 설정
    //             }
    //         } catch (err) {
    //             console.error('Error fetching billing data:', err);
    //             setError('데이터를 불러오는 중 오류가 발생했습니다. 기본값으로 표시됩니다.');
    //             setBillingData(defaultBillingData); // 기본값 설정
    //             setSelectedMonthInfo({ year: 2025, month: 4 }); // 기본값으로 2025년 4월 설정
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     loadBillingData();
    // }, []);

    const zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const generateDemoBillingData = () => {
        const now = new Date();
        const curYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevYM = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, '0')}`;
        const daysInLastMonth = new Date(now.getFullYear(), now.getMonth(), 0).getDate();

        const lastDailyCosts = Array.from({ length: daysInLastMonth }, (_, i) => ({
            date: `${prevYM}-${String(i + 1).padStart(2, '0')}`,
            dailyFee: Math.floor(Math.random() * 5000) + 10000,
            agentCount: Math.floor(Math.random() * 3) + 5,
            avgAgentCount: (Math.random() * 2 + 4).toFixed(2)
        }));
        const lastMonthCost = lastDailyCosts.reduce((s, d) => s + d.dailyFee, 0);
        const thisMonthCost = Math.floor(lastMonthCost * 0.6);
        const forecastCost = Math.floor(thisMonthCost / Math.max(now.getDate(), 1) * daysInLastMonth);

        const yearMonthOptions = [curYM, prevYM];

        const makeBillingDetail = (ym) => {
            const isCurrentMonth = ym === curYM;
            const totalMonthlyFee = isCurrentMonth ? thisMonthCost : lastMonthCost;
            const serverCost = 600000; // 서버 2대 기본비용

            // 에이전트 기반 비용 (서버 비용 제외)
            const agentBasedFee = totalMonthlyFee - serverCost;

            // 구간별 요금 계산 (데모용 현실적인 수치)
            const avgAgentCount = parseFloat((Math.random() * 2 + 4.5).toFixed(4)); // 4.5 ~ 6.5
            const maxAgentCount = Math.ceil(avgAgentCount) + Math.floor(Math.random() * 2); // 평균보다 1~2 높음
            const basePricePerAgent = 50000; // 에이전트당 기본 단가

            // 구간 할인 정보 (tierDiscountInfo) - BillingDetails가 JSON.parse해서 읽음
            const tier1Agents = Math.min(avgAgentCount, 3);
            const tier2Agents = Math.min(Math.max(avgAgentCount - 3, 0), 5);
            const tier3Agents = Math.max(avgAgentCount - 8, 0);

            const tier1Fee = Math.round(tier1Agents * basePricePerAgent);
            const tier2Fee = Math.round(tier2Agents * basePricePerAgent * 0.9);
            const tier3Fee = Math.round(tier3Agents * basePricePerAgent * 0.8);

            const tier2Discount = Math.round(tier2Agents * basePricePerAgent * 0.1);
            const tier3Discount = Math.round(tier3Agents * basePricePerAgent * 0.2);
            const totalTierDiscount = tier2Discount + tier3Discount;

            const tierDiscountInfo = JSON.stringify({
                applied_tiers: [
                    {
                        tier_level: 'Tier 1',
                        agent_count_from: 0,
                        agent_count_to: 3,
                        agents_in_tier: tier1Agents.toFixed(4),
                        price_per_agent: basePricePerAgent,
                        monthly_fee_for_tier: tier1Fee,
                        discount_rate: 0,
                        discount_type: 'percent',
                        tier_discount: 0
                    },
                    {
                        tier_level: 'Tier 2',
                        agent_count_from: 4,
                        agent_count_to: 8,
                        agents_in_tier: tier2Agents.toFixed(4),
                        price_per_agent: basePricePerAgent,
                        monthly_fee_for_tier: tier2Fee + tier2Discount,
                        discount_rate: 10,
                        discount_type: 'percent',
                        tier_discount: tier2Discount
                    },
                    ...(tier3Agents > 0 ? [{
                        tier_level: 'Tier 3',
                        agent_count_from: 9,
                        agent_count_to: 999,
                        agents_in_tier: tier3Agents.toFixed(4),
                        price_per_agent: basePricePerAgent,
                        monthly_fee_for_tier: tier3Fee + tier3Discount,
                        discount_rate: 20,
                        discount_type: 'percent',
                        tier_discount: tier3Discount
                    }] : [])
                ]
            });

            const sdkFee = tier1Fee + tier2Fee + tier3Fee + serverCost;

            // CF 서비스 청구 내역
            const cfCallCount = Math.floor(Math.random() * 2000) + 800;
            const cfDeployCount = Math.floor(Math.random() * 50) + 10;
            const cfCallFee = cfCallCount * 5;
            const cfDeployFee = cfDeployCount * 1000;
            const cfTotalFee = cfCallFee + cfDeployFee;

            // API 서비스 청구 내역
            const apiGatewayCount = Math.floor(Math.random() * 5000) + 1000;
            const apiProcessCount = Math.floor(Math.random() * 3000) + 500;
            const apiGatewayFee = apiGatewayCount * 3;
            const apiProcessFee = apiProcessCount * 7;
            const apiTotalFee = apiGatewayFee + apiProcessFee;

            const grandTotalFee = sdkFee + cfTotalFee + apiTotalFee;
            const discountFee = totalTierDiscount;
            const finalFee = grandTotalFee - discountFee;

            return {
                yearMonth: ym,
                totalMonthlyFee: grandTotalFee,
                discountFee,
                finalFee,
                pricingPolicyName: 'SaaS Standard',
                avgAgentCount: avgAgentCount.toFixed(2),
                maxAgentCount,
                basePricePerAgent,
                tierDiscountInfo,
                policyServerCnt: 2,
                option: JSON.stringify({ policy: { price: 300000 } }),
                finalAmount: !isCurrentMonth,
                finalized_at: !isCurrentMonth ? `${ym}-${daysInLastMonth}T23:59:00` : null,
                items: {
                    SDK: [
                        { name: '에이전트 과금 (구간별)', amount: tier1Fee + tier2Fee + tier3Fee },
                        { name: 'Policy 서버 운영', amount: serverCost }
                    ],
                    CF: [
                        { name: 'Cloud Function 호출', amount: cfCallFee },
                        { name: '배포 관리', amount: cfDeployFee }
                    ],
                    API: [
                        { name: 'API Gateway 요청', amount: apiGatewayFee },
                        { name: 'API 요청 처리', amount: apiProcessFee }
                    ]
                }
            };
        };

        return {
            data: {
                lastMonthCost,
                thisMonthCost,
                forecastCost,
                lastDailyCosts,
                yearMonthOptions,
                billingDetailsList: yearMonthOptions.map(makeBillingDetail)
            }
        };
    };

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = isDemoMode() ? generateDemoBillingData() : await fetchBillingData(zoneId);
                const data = res?.data;
                if (
                    data &&
                    Array.isArray(data.yearMonthOptions) &&
                    data.yearMonthOptions.length > 0
                ) {
                    setBillingData(data);
                    setSelectedYearMonth(data.yearMonthOptions[0]);
                    const map = {};
                    (data.billingDetailsList || []).forEach(d => {
                        map[d.yearMonth] = d;
                    });
                    setDetailsMap(map);
                } else {
                    setBillingData(defaultBillingData);
                    setSelectedYearMonth('2025-04');
                }
            } catch (e) {
                setError('데이터를 불러오는 중 오류가 발생했습니다.');
                setBillingData(defaultBillingData);
                setSelectedYearMonth('2025-04');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // 월 변경 핸들러 (추후 API 분리 대응)
    const handleMonthChange = async (yearMonth) => {
        setSelectedYearMonth(yearMonth);
        if (!detailsMap[yearMonth]) {
            // 필요시 별도 API 호출
            try {
                const detail = await fetchBillingDetail(yearMonth, zoneId);
                setDetailsMap(prev => ({ ...prev, [yearMonth]: detail }));
            } catch (e) {
                setError('상세 요금 정보를 불러오지 못했습니다.');
            }
        }
    };

    if (loading) return <div>Loading...</div>;

    const effectiveBillingData = billingData || defaultBillingData;

    // 선택된 월에 대한 데이터 필터링
    // const getMonthlyDataForSelectedMonth = () => {
    //     if (!effectiveBillingData.monthlyData || effectiveBillingData.monthlyData.length === 0) {
    //         console.log('월별 데이터가 없습니다:', effectiveBillingData.monthlyData);
    //         return null;
    //     }

    //     const selectedMonthData = effectiveBillingData.monthlyData.find(item =>
    //         item.month === selectedMonthInfo?.month && item.year === selectedMonthInfo?.year
    //     );

    //     if (!selectedMonthData) {
    //         console.log(`${selectedMonthInfo?.year}년 ${selectedMonthInfo?.month}월 데이터를 찾을 수 없습니다.`);
    //         return null;
    //     }

    //     return selectedMonthData.serviceData || null;
    // };

    // 선택된 월의 상세 정보
    const selectedDetail = detailsMap[selectedYearMonth] ||
        (effectiveBillingData.billingDetailsList || []).find(d => d.yearMonth === selectedYearMonth) ||
        null;

    // 이번달/지난달 동일기간 누적비용 계산
    const today = new Date();
    const currentDay = today.getDate();
    const lastMonthSamePeriodCost = (effectiveBillingData.lastDailyCosts || [])
        .slice(0, currentDay - 1)
        .reduce((sum, d) => sum + (d.dailyFee || 0), 0);

    // 월 옵션
    const monthOptions = effectiveBillingData.yearMonthOptions || [];


    // 데이터 직접 접근
    const lastMonthCost = effectiveBillingData?.lastMonthCost || 0;
    const thisMonthCost = effectiveBillingData?.thisMonthCost || 0;
    const forecastCost = effectiveBillingData?.forecastCost || 0;

    // // 지난달 동일 기간 비용 계산 - 현재 날짜까지의 지난달 데이터 합산
    // const today = new Date();
    // const currentDay = today.getDate();
    // let lastMonthSamePeriodCost = 0;

    // if (effectiveBillingData?.lastDailyCosts && effectiveBillingData.lastDailyCosts.length > 0) {
    //     // 전날까지의 비용만 계산 (오늘이 10일이면 1일~9일까지, 즉 인덱스 0~8)
    //     const samePeriodLastMonth = effectiveBillingData.lastDailyCosts.slice(0, currentDay - 1);
    //     lastMonthSamePeriodCost = samePeriodLastMonth.reduce((sum, day) => sum + day.amount, 0);
    //     console.log(`지난달 1일부터 ${currentDay - 1}일까지의 비용:`, lastMonthSamePeriodCost);
    // }

    // const billingDetails = effectiveBillingData?.billingDetails || {};
    // const monthlyData = effectiveBillingData?.monthlyData || [];
    // const pricingPolicy = monthlyData?.pricingPolicy || { name: "", tiers: [] };

    // // 사용 가능한 월 데이터 추출 (연도와 함께)
    // const availableMonths = monthlyData.map(item => ({
    //     month: item.month,
    //     year: item.year
    // }));

    // console.log('Extracted data:', {
    //     lastMonthCost,
    //     thisMonthCost,
    //     forecastCost,
    //     totalAmount: billingDetails.totalAmount,
    //     discount: billingDetails.discount,
    //     finalAmount: billingDetails.finalAmount
    // });

    // // 월 상태에 따른 메시지 생성 함수 - 선택된 월 정보 반영
    // const getMonthStatus = (monthInfo) => {
    //     const today = new Date();
    //     const currentYear = today.getFullYear();
    //     const currentMonth = today.getMonth() + 1;

    //     // 현재 연도의 현재 월인 경우
    //     if (monthInfo.year === currentYear && monthInfo.month === currentMonth) {
    //         const yesterday = new Date(today);
    //         yesterday.setDate(today.getDate() - 1);
    //         return `(미확정 금액, ${yesterday.getMonth() + 1}월 ${yesterday.getDate()}일까지의 요금)`;
    //     }

    //     // 과거 월인지 미래 월인지 확인 (연도 고려)
    //     const isPastMonth = (monthInfo.year < currentYear) ||
    //         (monthInfo.year === currentYear && monthInfo.month < currentMonth);

    //     return isPastMonth ? '(확정 금액)' : '(미확정 금액)';
    // };

    // // 실제 billingDetails.items가 있으면 사용하고, 없으면 null 설정
    // const hasServiceData = billingDetails.items &&
    //     typeof billingDetails.items === 'object' &&
    //     Object.keys(billingDetails.items).length > 0;

    // const serviceDataByType = hasServiceData ? billingDetails.items : null;

    // // 총 금액 계산
    // const totalAmount = billingDetails.totalAmount || 0;
    // const discount = billingDetails.discount || 0;
    // const finalAmount = billingDetails.finalAmount || 0;

    // // 이번달 진행률 계산 추가 - today 변수 재사용
    // const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    // const monthCompletionPercentage = (today.getDate() / daysInMonth) * 100;
    // console.log('Month completion percentage:', monthCompletionPercentage);

    // console.log('Month data:', {
    //     thisMonthCost,
    //     lastMonthCost,
    //     forecastCost,
    //     totalAmount,
    //     discount,
    //     finalAmount
    // });

    // // 네임스페이스 모달 처리 함수
    // const handleNamespaceModalOpen = () => {
    //     console.log('네임스페이스별 상세 정보 모달 열기');
    //     setShowNamespaceModal(true);
    // };

    // const handleNamespaceModalClose = () => {
    //     setShowNamespaceModal(false);
    // };


    // // 선택된 월에 대한 청구 상세 정보 - 이 부분만 남기고 아래 중복 선언은 삭제
    // const selectedMonthServiceData = getMonthlyDataForSelectedMonth();

    // // 실제 표시할 서비스 데이터 결정 - 선택된 월의 데이터가 있으면 사용, 없으면 기본 데이터
    // const displayServiceData = selectedMonthServiceData || defaultServiceData;

    // console.log('최종 표시할 서비스 데이터:', displayServiceData);

    // // 선택된 ��에 대한 요금 정보 계산 수정
    // const getSelectedMonthBillingInfo = () => {
    //     if (!monthlyData || monthlyData.length === 0) {
    //         console.log('월별 요금 데이터가 없습니다.');
    //         return {
    //             totalAmount: 0,
    //             discount: 0,
    //             finalAmount: 0
    //         };
    //     }

    //     // 선택된 연도와 월에 해당하는 데이터 찾기
    //     const selectedMonthData = monthlyData.find(item =>
    //         item.month === selectedMonthInfo.month && item.year === selectedMonthInfo.year
    //     );

    //     if (!selectedMonthData) {
    //         console.log(`${selectedMonthInfo.year}년 ${selectedMonthInfo.month}월 요금 데이터를 찾을 수 없습니다.`);
    //         return {
    //             totalAmount: 0,
    //             discount: 0,
    //             finalAmount: 0
    //         };
    //     }

    //     const result = {
    //         totalAmount: selectedMonthData.totalAmount || 0,
    //         discount: selectedMonthData.discount || 0,
    //         finalAmount: selectedMonthData.finalAmount || 0
    //     };

    //     console.log(`${selectedMonthInfo.year}년 ${selectedMonthInfo.month}월 요금 정보:`, result);
    //     return result;
    // };

    // // // 선택된 월 변경 핸들러 수정
    // // const handleMonthChange = (value) => {
    // //     const [year, month] = value.split('-').map(Number);
    // //     console.log(`월 변경: ${selectedMonthInfo.year}년 ${selectedMonthInfo.month}월 -> ${year}년 ${month}월`);
    // //     setSelectedMonthInfo({ year, month });
    // // };

    // // 선택된 월에 대한 요금 정보 계산
    // const selectedMonthBilling = monthlyData.find(item =>
    //     item.month === selectedMonthInfo.month && item.year === selectedMonthInfo.year
    // ) || {
    //     totalAmount: 0, discount: 0, finalAmount: 0,
    //     pricingPolicy: { name: "", tiers: [] } // 기본값으로 빈 정책 추가
    // };

    return (
        <div className="container mt-4">
            {error && (
                <div className="alert alert-warning" role="alert">
                    {error}
                </div>
            )}
            <Row className="g-3 mb-3">
                <Col md={4}>
                    <MemoizedLastMonthCostChart
                        lastMonthCost={lastMonthCost}
                        dailyCosts={effectiveBillingData.lastDailyCosts}
                    />
                </Col>
                <Col md={4}>
                    <MemoizedBillingSummary
                        title="이번달 비용"
                        amount={thisMonthCost}
                        comparisonAmount={lastMonthSamePeriodCost}
                        progressPercentage={(today.getDate() / new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()) * 100}
                        showProgress={true}
                        isSamePeriodComparison={true}
                        decimalPlaces={2}
                        roundingMethod="round"
                    />
                </Col>
                <Col md={4}>
                    <MemoizedBillingSummary
                        title="예상 금액"
                        amount={forecastCost}
                        comparisonAmount={lastMonthCost}
                        showProgress={false}
                        isForecast={true}
                        decimalPlaces={2}
                        roundingMethod="round"
                    />
                </Col>
            </Row>

            {/* 서비스 타입 토글 */}
            <Row className="mb-3">
                <Col>
                    <div className="d-flex align-items-center">
                        <span className="me-3 fw-semi-bold text-700">서비스 선택</span>
                        <div className="btn-group" role="group">
                            {SERVICE_TYPES.map(({ key, label }) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`btn btn-sm ${serviceType === key ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setServiceType(key)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </Col>
            </Row>

            <Row className="g-3">
                <Col md={4}>
                    <MemoizedRealTimeUsageChart serviceType={serviceType} />
                </Col>
                <Col md={8}>
                    <Row className="g-3">
                        <Col md={12}>
                            <MemoizedTodayUsageChart serviceType={serviceType} />
                        </Col>
                        <Col md={12}>
                            <MemoizedUsageChart serviceType={serviceType} />
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* 청구 정보 구분선 추가 */}
            <Row className="mt-5 mb-4">
                <Col>
                    <h5 className="mb-0 text-primary position-relative">
                        <span className="bg-body pe-3 position-relative" style={{ zIndex: 1 }}>청구 정보</span>
                        <span className="border position-absolute top-50 translate-middle-y w-100 start-0 z-n1"></span>
                    </h5>
                </Col>
            </Row>

            <Row className="g-3 mt-4">
                <Col lg={6}>
                    <MemoizedBillingDetails
                        // monthlyData={monthlyData}
                        // selectedMonth={selectedMonthInfo}
                        // defaultServiceData={defaultServiceData}
                        // getMonthStatus={getMonthStatus}
                        // pricingPolicy={pricingPolicy}
                        detail={selectedDetail}
                        yearMonth={selectedYearMonth}
                        monthOptions={monthOptions}
                        onMonthChange={handleMonthChange}
                        isFinalAmount={!!selectedDetail?.finalAmount} // boolean 값 그대로 전달
                    />
                </Col>
                <Col lg={6}>
                    <MemoizedBillingSummary
                        // title="최종 요금"
                        // amount={selectedMonthBilling.totalAmount || 0}
                        // discount={selectedMonthBilling.discount || 0}
                        // finalAmount={selectedMonthBilling.finalAmount || 0}
                        // selectedMonth={selectedMonthInfo}
                        // onMonthChange={handleMonthChange}
                        // pricingPolicy={selectedMonthBilling.pricingPolicy}
                        // availableMonths={availableMonths}
                        // decimalPlaces={2} // 소수점 둘째자리까지 표시
                        // roundingMethod="round" // 셋째자리에서 반올림
                        title="최종 요금"
                        amount={selectedDetail?.totalMonthlyFee || 0}
                        discount={selectedDetail?.discountFee || 0}
                        finalAmount={selectedDetail?.finalFee || 0}
                        detail={selectedDetail} // detail 전체 전달
                        selectedMonth={selectedYearMonth}
                        onMonthChange={handleMonthChange}
                        availableMonths={monthOptions}
                        isFinal
                        isFinalAmount={!!selectedDetail?.finalAmount} // boolean 값 그대로 전달
                        pricingPolicy={selectedDetail?.pricingPolicyName}
                    />
                </Col>
            </Row>

        </div>
    );
};

export default BillingDashboard;